// Mirrors iOS RestaurantPaymentsView: per-day earnings (confirmed+ orders,
// restaurantPayout ?? subtotal*0.9) keyed "restaurant_{id}_{dayTs}". A day is
// paid when its doc in /payouts has paymentStatus == "Paid" — written when the
// admin marks it paid, so the business sees real pending vs paid amounts.
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Clock, Info, Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChefHeader from '../shared/ui/ChefHeader';
import { LoadingView } from '../shared/ui';
import { listenToRestaurantOrders, listenToRestaurantPayouts } from '../shared/services/orderService';
import { formatCurrency, formatDate, toDate } from '../shared/models';
import { isCancelled, payoutOf } from '../components/analytics/analyticsUtils';

// Statuses that count toward restaurant earnings (iOS: confirmed and later).
const EARNING_STATUSES = new Set([
  'confirmed', 'preparing', 'ready', 'on my way', 'arrived',
  'in delivery', 'delivered', 'completed',
]);

const payoutKey = (restaurantId, day) =>
  `restaurant_${restaurantId}_${(day.getTime() / 1000).toFixed(1)}`;

function Section({ title, children }) {
  return (
    <div>
      <p className="text-[12px] font-medium text-gray-400 uppercase tracking-wide px-4 mb-1.5">
        {title}
      </p>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden divide-y divide-gray-100">
        {children}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, sub = false }) {
  return (
    <div className="flex items-center justify-between px-4 py-3.5">
      <span className="text-sm text-gray-900">{label}</span>
      <span className={`${sub ? 'text-xs text-gray-400' : 'text-sm font-semibold text-gray-900'}`}>
        {value}
      </span>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 px-3 py-3">
      <div className="flex items-center gap-1.5">
        <Icon size={14} className={color} />
        <p className="text-[11px] text-gray-400 truncate">{title}</p>
      </div>
      <p className="text-lg font-extrabold text-gray-900 mt-0.5">{value}</p>
    </div>
  );
}

export default function PaymentsPage() {
  const { restaurantId } = useAuth();
  const [orders, setOrders] = useState([]);
  const [payoutDocs, setPayoutDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId) { setLoading(false); return undefined; }
    let gotOrders = false;
    let gotPayouts = false;
    const done = () => { if (gotOrders && gotPayouts) setLoading(false); };
    const un1 = listenToRestaurantOrders(restaurantId, (list) => {
      setOrders(list);
      gotOrders = true;
      done();
    }, () => { gotOrders = true; done(); });
    const un2 = listenToRestaurantPayouts(restaurantId, (list) => {
      setPayoutDocs(list);
      gotPayouts = true;
      done();
    }, () => { gotPayouts = true; done(); });
    return () => { un1 && un1(); un2 && un2(); };
  }, [restaurantId]);

  // Per-day earnings keyed by the payout record id (same scheme as admin).
  const dayEarnings = useMemo(() => {
    const byDay = new Map();
    for (const o of orders) {
      if (!o.createdAt) continue;
      const status = (o.rawStatus || o.status || '').toLowerCase();
      if (isCancelled(o) || !EARNING_STATUSES.has(status)) continue;
      const day = new Date(o.createdAt);
      day.setHours(0, 0, 0, 0);
      const key = payoutKey(restaurantId, day);
      const e = byDay.get(key) || { id: key, date: day, amount: 0, orderCount: 0 };
      e.amount += payoutOf(o);
      e.orderCount += 1;
      byDay.set(key, e);
    }
    return [...byDay.values()];
  }, [orders, restaurantId]);

  const paidDocs = useMemo(
    () => payoutDocs
      .filter((d) => (d.paymentStatus || '').toLowerCase() === 'paid')
      .sort((a, b) => (toDate(b.paidAt)?.getTime() || 0) - (toDate(a.paidAt)?.getTime() || 0)),
    [payoutDocs],
  );

  const paidIds = useMemo(() => new Set(paidDocs.map((d) => d.id)), [paidDocs]);
  const pendingDays = useMemo(
    () => dayEarnings.filter((d) => !paidIds.has(d.id)),
    [dayEarnings, paidIds],
  );
  const pendingTotal = pendingDays.reduce((s, d) => s + d.amount, 0);
  const paidTotal = paidDocs.reduce((s, d) => s + (Number(d.amount ?? d.totalEarnings) || 0), 0);
  const lastPayout = paidDocs[0];

  return (
    <div className="pb-6">
      <ChefHeader title="Payments" />
      {loading ? (
        <LoadingView label="Loading payments…" />
      ) : (
        <div className="px-4 pt-4 space-y-5">
          {/* Stat cards: unpaid vs paid (iOS AdminStatCardRow) */}
          <div className="flex gap-3">
            <StatCard
              title={`Unpaid (${pendingDays.length})`}
              value={formatCurrency(pendingTotal)}
              icon={Clock}
              color="text-orange-500"
            />
            <StatCard
              title={`Paid (${paidDocs.length})`}
              value={formatCurrency(paidTotal)}
              icon={CheckCircle2}
              color="text-green-600"
            />
          </div>

          {/* Hero balance card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
            <span className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
              <Wallet size={22} className="text-green-600" />
            </span>
            <div>
              <p className="text-xs text-gray-400">Pending Payouts</p>
              <p className="text-2xl font-extrabold text-gray-900">{formatCurrency(pendingTotal)}</p>
              <p className="text-[11px] text-gray-400">
                {pendingDays.length} unpaid day{pendingDays.length === 1 ? '' : 's'} · 90% of order subtotals
              </p>
            </div>
          </div>

          <Section title="Payout Summary">
            <SummaryRow label="Pending Payouts" value={formatCurrency(pendingTotal)} />
            <SummaryRow
              label="Last Payout"
              sub
              value={lastPayout
                ? `${formatCurrency(Number(lastPayout.amount ?? lastPayout.totalEarnings) || 0)} on ${formatDate(toDate(lastPayout.paidAt) || toDate(lastPayout.updatedAt))}`
                : 'No payouts yet'}
            />
          </Section>

          {paidDocs.length > 0 && (
            <Section title="Payout History">
              {paidDocs.slice(0, 30).map((d) => (
                <div key={d.id} className="flex items-center gap-3 px-4 py-3">
                  <CheckCircle2 size={18} className="text-green-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">
                      {formatCurrency(Number(d.amount ?? d.totalEarnings) || 0)}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {formatDate(toDate(d.paidAt) || toDate(d.updatedAt))}
                      {d.paymentReferenceNumber ? ` · Ref ${d.paymentReferenceNumber}` : ''}
                    </p>
                  </div>
                  <span className="text-[11px] font-semibold text-green-700 bg-green-50 rounded-full px-2 py-0.5">
                    Paid
                  </span>
                </div>
              ))}
            </Section>
          )}

          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3">
            <Info size={16} className="text-blue-500 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-700">
              Your first payout takes 2 days. After that, payouts are processed daily by the CheF team.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
